import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Transaction,
  FormState,
  ActiveField,
  FilterTab,
  TransactionType,
} from "./types";
import {
  ACCOUNTS,
  EXPENSE_CATEGORIES,
  INCOME_CATEGORIES,
  CATEGORY_EMOJIS,
  ACCOUNT_COLORS,
  INITIAL_TRANSACTIONS,
  formatRupiah,
  parseAmount,
} from "./data";
import { format, isToday, isYesterday } from "date-fns";
import DatePicker from "./DatePicker";

const FIELD_ORDER: ActiveField[] = [
  "date",
  "account",
  "category",
  "note",
  "amount",
];

export default function App() {
  const [transactionType, setTransactionType] =
    useState<TransactionType>("expense");
  const [userName, setUserName] = useState("Parikesit");
  const [editingName, setEditingName] = useState(false);
  const [form, setForm] = useState<FormState>({
    date: "",
    account: "",
    category: "",
    note: "",
    amount: "",
  });
  const [selectedDateObj, setSelectedDateObj] = useState<Date | null>(null);
  const [activeField, setActiveField] = useState<ActiveField>(null);
  const [transactions, setTransactions] =
    useState<Transaction[]>(INITIAL_TRANSACTIONS);
  const [filterTab, setFilterTab] = useState<FilterTab>("date");
  const [search, setSearch] = useState("");
  const [dropdownSearch, setDropdownSearch] = useState("");

  const dateRef = useRef<HTMLInputElement>(null);
  const accountRef = useRef<HTMLInputElement>(null);
  const categoryRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLInputElement>(null);
  const amountRef = useRef<HTMLInputElement>(null);
  const dateFieldRef = useRef<HTMLDivElement>(null);

  const fieldRefs: Record<string, React.RefObject<HTMLInputElement | null>> = {
    date: dateRef,
    account: accountRef,
    category: categoryRef,
    note: noteRef,
    amount: amountRef,
  };

  const categories =
    transactionType === "expense" ? EXPENSE_CATEGORIES : INCOME_CATEGORIES;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        activeField === "date" &&
        dateFieldRef.current &&
        !dateFieldRef.current.contains(e.target as Node)
      ) {
        setActiveField(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeField]);

  useEffect(() => {
    if (activeField && fieldRefs[activeField]?.current) {
      fieldRefs[activeField].current?.focus();
    }
    setDropdownSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeField]);

  const goNextField = useCallback(() => {
    if (!activeField) return;
    const idx = FIELD_ORDER.indexOf(activeField);
    if (idx < FIELD_ORDER.length - 1) setActiveField(FIELD_ORDER[idx + 1]);
  }, [activeField]);

  const goPrevField = useCallback(() => {
    if (!activeField) return;
    const idx = FIELD_ORDER.indexOf(activeField);
    if (idx > 0) setActiveField(FIELD_ORDER[idx - 1]);
  }, [activeField]);

  const submitTransaction = useCallback(() => {
    if (!form.date || !form.account || !form.category || !form.amount) return;
    const txDate = selectedDateObj || new Date();
    const newTx: Transaction = {
      id: Date.now().toString(),
      date: txDate,
      account: form.account,
      category: form.category,
      note: form.note,
      amount: parseAmount(form.amount),
      type: transactionType,
    };
    setTransactions((prev) => [newTx, ...prev]);
    setForm({ date: "", account: "", category: "", note: "", amount: "" });
    setSelectedDateObj(null);
    setActiveField(null);
  }, [form, transactionType, selectedDateObj]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab" && !e.shiftKey) {
        e.preventDefault();
        if (activeField === "amount") submitTransaction();
        else goNextField();
        return;
      }
      if (e.key === "Tab" && e.shiftKey) {
        e.preventDefault();
        goPrevField();
        return;
      }
      if (e.key === "Enter") {
        if (activeField === "account" || activeField === "category") return;
        if (activeField === "amount") {
          submitTransaction();
          return;
        }
        goNextField();
        return;
      }
      if (
        (e.key === "t" || e.key === "T") &&
        activeField === "date" &&
        document.activeElement === dateRef.current
      ) {
        e.preventDefault();
        const today = new Date();
        setSelectedDateObj(today);
        setForm((f) => ({ ...f, date: format(today, "d MMM yyyy") }));
        goNextField();
      }
      if (
        (e.key === "y" || e.key === "Y") &&
        activeField === "date" &&
        document.activeElement === dateRef.current
      ) {
        e.preventDefault();
        const y = new Date();
        y.setDate(y.getDate() - 1);
        setSelectedDateObj(y);
        setForm((f) => ({ ...f, date: format(y, "d MMM yyyy") }));
        goNextField();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeField, goNextField, goPrevField, submitTransaction]);

  const handleDropdownSelect = (
    field: "account" | "category",
    value: string,
  ) => {
    setForm((f) => ({ ...f, [field]: value }));
    goNextField();
  };

  const filteredAccounts = ACCOUNTS.filter((a) =>
    a.toLowerCase().includes(dropdownSearch.toLowerCase()),
  );
  const filteredCategories = categories.filter((c) =>
    c.toLowerCase().includes(dropdownSearch.toLowerCase()),
  );

  const totalExpense = transactions
    .filter((t) => t.type === "expense")
    .reduce((s, t) => s + t.amount, 0);
  const totalIncome = transactions
    .filter((t) => t.type === "income")
    .reduce((s, t) => s + t.amount, 0);
  const totalNet = totalIncome - totalExpense;

  const filteredTransactions = transactions.filter((tx) => {
    if (!search) return true;
    return (
      tx.note.toLowerCase().includes(search.toLowerCase()) ||
      tx.category.toLowerCase().includes(search.toLowerCase()) ||
      tx.account.toLowerCase().includes(search.toLowerCase())
    );
  });

  const groupBy = <T extends Transaction>(
    arr: T[],
    key: keyof T,
  ): Record<string, T[]> =>
    arr.reduce((g: Record<string, T[]>, item) => {
      const k = String(item[key]);
      if (!g[k]) g[k] = [];
      g[k].push(item);
      return g;
    }, {});

  const groupedByDate = filteredTransactions.reduce(
    (g: Record<string, Transaction[]>, tx) => {
      const k = format(tx.date, "yyyy-MM-dd");
      if (!g[k]) g[k] = [];
      g[k].push(tx);
      return g;
    },
    {},
  );

  const groupedByCategory = groupBy(filteredTransactions, "category");
  const groupedByAccount = groupBy(filteredTransactions, "account");

  const getDateLabel = (d: string) => {
    const date = new Date(d);
    if (isToday(date)) return "TODAY";
    if (isYesterday(date)) return "YESTERDAY";
    return format(date, "d MMM yyyy").toUpperCase();
  };

  const getDayTotal = (txs: Transaction[]) => {
    const inc = txs
      .filter((t) => t.type === "income")
      .reduce((s, t) => s + t.amount, 0);
    const exp = txs
      .filter((t) => t.type === "expense")
      .reduce((s, t) => s + t.amount, 0);
    return inc - exp;
  };

  const renderDropdown = (field: "account" | "category") => {
    const items = field === "account" ? filteredAccounts : filteredCategories;
    return (
      <div
        className="absolute top-full left-0 z-50 bg-white rounded-xl shadow-xl border border-gray-100 min-w-[200px] mt-1"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2.5 border-b border-gray-100">
          <input
            autoFocus
            className="w-full text-sm text-gray-600 placeholder-gray-300 outline-none bg-transparent"
            placeholder="Search to select"
            value={dropdownSearch}
            onChange={(e) => setDropdownSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && items.length > 0)
                handleDropdownSelect(field, items[0]);
              if (e.key === "Tab") {
                e.preventDefault();
                if (items.length > 0) handleDropdownSelect(field, items[0]);
              }
              if (e.key === "Escape") setActiveField(null);
              if (e.key === "Backspace" && dropdownSearch === "") goPrevField();
              if (e.key === "ArrowDown" && items.length > 0)
                handleDropdownSelect(field, items[0]);
            }}
          />
        </div>
        <div className="py-1 max-h-48 overflow-y-auto">
          {items.map((item, i) => (
            <button
              key={item}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2.5 transition-colors"
              onClick={() => handleDropdownSelect(field, item)}
            >
              <span className="text-gray-300 font-mono text-xs w-4">
                {i + 1}
              </span>
              <span className="text-gray-800">{item}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const TxIcon = ({ tx }: { tx: Transaction }) => (
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${tx.type === "income" ? "bg-green-50 text-green-500" : "bg-red-50 text-red-400"}`}
    >
      {tx.type === "income" ? "↙" : "↗"}
    </div>
  );

  const TxRow = ({
    tx,
    showDate = false,
  }: {
    tx: Transaction;
    showDate?: boolean;
  }) => (
    <div className="flex items-center gap-3 px-4 py-3 transaction-row cursor-pointer border-b border-gray-50 last:border-0">
      <TxIcon tx={tx} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span
            className="text-xs font-semibold"
            style={{ color: ACCOUNT_COLORS[tx.account] || "#3b82f6" }}
          >
            {tx.account}
          </span>
          {showDate && (
            <>
              <span className="text-gray-300 text-xs">/</span>
              <span className="text-xs text-gray-400">
                {format(tx.date, "d MMM")}
              </span>
            </>
          )}
          <span className="text-gray-200 text-xs">/</span>
          {CATEGORY_EMOJIS[tx.category] && (
            <span className="text-xs">{CATEGORY_EMOJIS[tx.category]}</span>
          )}
          <span className="text-xs text-gray-400">{tx.category}</span>
        </div>
        <div className="text-sm text-gray-700 truncate">
          {tx.note || <span className="text-gray-300">–</span>}
        </div>
      </div>
      <div className="text-sm font-medium font-mono flex-shrink-0 text-gray-700">
        {formatRupiah(tx.amount)}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="bg-white border-b border-gray-100 px-6 py-0 flex items-center justify-between h-12">
        <span className="text-sm font-bold text-gray-800 tracking-tight">
          Expense Tracker
        </span>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500 uppercase">
            {userName.trim().charAt(0) || "?"}
          </div>
          {editingName ? (
            <input
              autoFocus
              className="text-xs font-medium text-gray-700 border-b border-gray-400 outline-none bg-transparent w-24"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onBlur={() => setEditingName(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Escape")
                  setEditingName(false);
              }}
            />
          ) : (
            <button
              onClick={() => setEditingName(true)}
              className="text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              {userName || "My Name"}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border-b border-gray-100 px-4 py-1.5 flex items-center gap-0.5 text-xs text-gray-500 overflow-x-auto whitespace-nowrap select-none">
        <button
          onClick={() => setTransactionType("expense")}
          className={`px-2.5 py-1 rounded-md text-xs font-medium mr-1 transition-colors ${transactionType === "expense" ? "bg-red-50 text-red-500" : "text-gray-400 hover:bg-gray-50"}`}
        >
          Expense
        </button>
        <button
          onClick={() => setTransactionType("income")}
          className={`px-2.5 py-1 rounded-md text-xs font-medium mr-2 transition-colors ${transactionType === "income" ? "bg-green-50 text-green-500" : "text-gray-400 hover:bg-gray-50"}`}
        >
          Income
        </button>
        <span className="text-gray-200 mx-1">|</span>
        <span className="px-1.5 text-gray-400">
          Tab <span className="text-gray-300">Next</span>
        </span>
        <span className="px-1.5 text-gray-400">
          Enter <span className="text-gray-300">Submit</span>
        </span>
        <span className="text-gray-200 mx-1">|</span>
        <span className="px-1.5 text-gray-400">
          <kbd className="bg-gray-100 px-1 py-0.5 rounded text-[10px] font-mono">
            T
          </kbd>{" "}
          <span className="text-gray-300">Today</span>
        </span>
        <span className="px-1.5 text-gray-400">
          <kbd className="bg-gray-100 px-1 py-0.5 rounded text-[10px] font-mono">
            Y
          </kbd>{" "}
          <span className="text-gray-300">Yesterday</span>
        </span>
      </div>

      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="flex items-stretch divide-x divide-gray-100">
          <div
            ref={dateFieldRef}
            className={`relative px-4 py-3 cursor-pointer transition-colors ${activeField === "date" ? "bg-gray-50" : "hover:bg-gray-50/50"}`}
            style={{ minWidth: "150px", flex: "1.2" }}
            onClick={() => setActiveField("date")}
          >
            <div className="text-[10px] font-semibold text-gray-400 tracking-widest uppercase mb-1.5">
              Date
            </div>
            <input
              ref={dateRef}
              className="w-full text-sm text-gray-700 bg-transparent placeholder-gray-300 font-mono outline-none cursor-pointer"
              placeholder="–"
              value={form.date}
              readOnly
              onFocus={() => setActiveField("date")}
            />
            {activeField === "date" && (
              <>
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-800 rounded-full" />
                <DatePicker
                  selectedDate={selectedDateObj}
                  onSelect={(date) => {
                    setSelectedDateObj(date);
                    setForm((f) => ({
                      ...f,
                      date: format(date, "d MMM yyyy"),
                    }));
                  }}
                  onClose={() => goNextField()}
                />
              </>
            )}
          </div>

          <div
            className={`relative px-4 py-3 cursor-pointer transition-colors ${activeField === "account" ? "bg-gray-50" : "hover:bg-gray-50/50"}`}
            style={{ minWidth: "140px", flex: "1" }}
            onClick={() => setActiveField("account")}
          >
            <div className="text-[10px] font-semibold text-gray-400 tracking-widest uppercase mb-1.5">
              Account
            </div>
            <div className="text-sm text-gray-700">
              {form.account || <span className="text-gray-300">–</span>}
            </div>
            <input
              ref={accountRef}
              className="sr-only"
              readOnly
              onFocus={() => setActiveField("account")}
            />
            {activeField === "account" && (
              <>
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-800 rounded-full" />
                {renderDropdown("account")}
              </>
            )}
          </div>

          <div
            className={`relative px-4 py-3 cursor-pointer transition-colors ${activeField === "category" ? "bg-gray-50" : "hover:bg-gray-50/50"}`}
            style={{ minWidth: "150px", flex: "1" }}
            onClick={() => setActiveField("category")}
          >
            <div className="text-[10px] font-semibold text-gray-400 tracking-widest uppercase mb-1.5">
              Category
            </div>
            <div className="text-sm text-gray-700">
              {form.category || <span className="text-gray-300">–</span>}
            </div>
            <input
              ref={categoryRef}
              className="sr-only"
              readOnly
              onFocus={() => setActiveField("category")}
            />
            {activeField === "category" && (
              <>
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-800 rounded-full" />
                {renderDropdown("category")}
              </>
            )}
          </div>

          <div
            className={`relative px-4 py-3 cursor-pointer transition-colors ${activeField === "note" ? "bg-gray-50" : "hover:bg-gray-50/50"}`}
            style={{ minWidth: "180px", flex: "2" }}
            onClick={() => setActiveField("note")}
          >
            <div className="text-[10px] font-semibold text-gray-400 tracking-widest uppercase mb-1.5">
              Note
            </div>
            <input
              ref={noteRef}
              className="w-full text-sm text-gray-700 bg-transparent placeholder-gray-300 outline-none"
              placeholder="Add Notes"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              onFocus={() => setActiveField("note")}
              onKeyDown={(e) => {
                if (e.key === "Tab") {
                  e.preventDefault();
                  goNextField();
                }
                if (e.key === "Enter") goNextField();
              }}
            />
            {activeField === "note" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-800 rounded-full" />
            )}
          </div>

          <div
            className={`relative px-4 py-3 cursor-pointer transition-colors ${activeField === "amount" ? "bg-gray-50" : "hover:bg-gray-50/50"}`}
            style={{ minWidth: "150px", flex: "1.2" }}
            onClick={() => setActiveField("amount")}
          >
            <div className="text-[10px] font-semibold text-gray-400 tracking-widest uppercase mb-1.5">
              Amount
            </div>
            <input
              ref={amountRef}
              className="w-full text-sm text-gray-700 bg-transparent placeholder-gray-300 font-mono outline-none"
              placeholder="–"
              value={form.amount}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, "");
                setForm((f) => ({
                  ...f,
                  amount: val
                    ? parseInt(val).toLocaleString("id-ID").replace(/,/g, ".")
                    : "",
                }));
              }}
              onFocus={() => setActiveField("amount")}
              onKeyDown={(e) => {
                if (e.key === "Tab" || e.key === "Enter") {
                  e.preventDefault();
                  submitTransaction();
                }
              }}
            />
            {activeField === "amount" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-800 rounded-full" />
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-100">
        <div className="flex divide-x divide-gray-100">
          <div className="flex-1 px-4 py-3 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-400 flex items-center gap-1.5 uppercase tracking-wider">
              EXPENSE <span className="text-gray-300">↗</span>
            </span>
            <span className="text-sm font-semibold text-red-500 font-mono">
              {formatRupiah(totalExpense)}
            </span>
          </div>
          <div className="flex-1 px-4 py-3 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-400 flex items-center gap-1.5 uppercase tracking-wider">
              INCOME <span className="text-gray-300">↙</span>
            </span>
            <span className="text-sm font-semibold text-green-500 font-mono">
              {formatRupiah(totalIncome)}
            </span>
          </div>
          <div className="flex-1 px-4 py-3 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              TOTAL
            </span>
            <span
              className={`text-sm font-semibold font-mono ${totalNet >= 0 ? "text-green-500" : "text-red-500"}`}
            >
              {formatRupiah(totalNet)}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-100 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-1">
          {(["date", "category", "account"] as FilterTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setFilterTab(tab)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filterTab === tab ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100"}`}
            >
              By {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-full px-3 py-1.5">
          <svg
            className="w-3 h-3 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            className="text-xs text-gray-600 bg-transparent placeholder-gray-400 w-32 outline-none"
            placeholder="Search transaction"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div
        className="py-3"
        onClick={() => {
          if (activeField === "account" || activeField === "category")
            setActiveField(null);
        }}
      >
        {filteredTransactions.length === 0 && (
          <div className="text-center py-20 text-gray-300 text-sm">
            No transactions found
          </div>
        )}

        {filterTab === "date" &&
          Object.keys(groupedByDate)
            .sort((a, b) => b.localeCompare(a))
            .map((key) => {
              const dayTxs = groupedByDate[key];
              const dayTotal = getDayTotal(dayTxs);
              return (
                <div key={key} className="mb-2">
                  <div className="flex items-center justify-between px-4 py-1.5">
                    <span className="text-[11px] font-semibold text-gray-400 tracking-widest">
                      {getDateLabel(key)}
                    </span>
                    <span
                      className={`text-[11px] font-semibold font-mono ${dayTotal >= 0 ? "text-green-500" : "text-red-400"}`}
                    >
                      {dayTotal >= 0 ? "+" : ""}
                      {formatRupiah(Math.abs(dayTotal))}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl mx-3 overflow-hidden shadow-sm border border-gray-100">
                    {dayTxs.map((tx) => (
                      <TxRow key={tx.id} tx={tx} />
                    ))}
                  </div>
                </div>
              );
            })}

        {filterTab === "category" &&
          Object.entries(groupedByCategory).map(([cat, txs]) => {
            const total = txs.reduce((s, t) => s + t.amount, 0);
            return (
              <div key={cat} className="mb-2">
                <div className="flex items-center justify-between px-4 py-1.5">
                  <span className="text-[11px] font-semibold text-gray-400 tracking-widest flex items-center gap-1.5">
                    {CATEGORY_EMOJIS[cat]} {cat.toUpperCase()}
                  </span>
                  <span className="text-[11px] font-semibold font-mono text-gray-400">
                    {formatRupiah(total)}
                  </span>
                </div>
                <div className="bg-white rounded-xl mx-3 overflow-hidden shadow-sm border border-gray-100">
                  {txs.map((tx) => (
                    <TxRow key={tx.id} tx={tx} showDate />
                  ))}
                </div>
              </div>
            );
          })}

        {filterTab === "account" &&
          Object.entries(groupedByAccount).map(([acc, txs]) => {
            const total = txs.reduce((s, t) => s + t.amount, 0);
            return (
              <div key={acc} className="mb-2">
                <div className="flex items-center justify-between px-4 py-1.5">
                  <span
                    className="text-[11px] font-semibold tracking-widest"
                    style={{ color: ACCOUNT_COLORS[acc] || "#888" }}
                  >
                    {acc}
                  </span>
                  <span className="text-[11px] font-semibold font-mono text-gray-400">
                    {formatRupiah(total)}
                  </span>
                </div>
                <div className="bg-white rounded-xl mx-3 overflow-hidden shadow-sm border border-gray-100">
                  {txs.map((tx) => (
                    <TxRow key={tx.id} tx={tx} showDate />
                  ))}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
